'use client';

import React from 'react';

import { LoadingBall } from '@depop/web-ui-kit/LoadingBall';

import { BrowseProductCard } from '../BrowseProductCard';
import { EmptyResultsList } from '../EmptyResultsList';

import styles from './styles.module.css';

import { InfiniteScroll } from '@/components/InfiniteScroll';
import { ProductImpressionHandler } from '@/components/ProductImpressionHandler';
import { SFYSignUpSuccessModal } from '@/components/SFYSignUpSuccessModal';
import { TeaseSuggestedForYou } from '@/components/TeaseSuggestedForYou';
import { useShowComponentOnce } from '@/modules/dom/hooks/useShowComponentOnce';
import { ExperimentWrapper } from '@/modules/experiments/ExperimentWrapper';
import { IS_PLA_REFERER } from '@/modules/product/constants';
import { getProductsFromPaginatedResponse } from '@/modules/product/helpers/getProductsFromPaginatedResponse';
import { ITEMS_PER_PAGE } from '@/modules/search/constants';
import { useProductsSearchWithFallback } from '@/modules/search/useProductsSearchWithFallback';
import { type ClickHandlerArgs } from '@/modules/search/useSearchTracking';
import { getSessionStorageItem } from '@/modules/storage';

const SFY_SHOWN_EXPLORE_PAGE = 'SFY_SHOWN_EXPLORE_PAGE';

export function BrowseProductsList({
  handleProductClick,
  query,
}: {
  handleProductClick: (arg: ClickHandlerArgs) => void;
  query?: string;
}) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useProductsSearchWithFallback(query);
  const isPLAReferer = getSessionStorageItem(IS_PLA_REFERER) === 'true';

  const [teaseSFYRef, sfyTeaseUnseen] = useShowComponentOnce(
    SFY_SHOWN_EXPLORE_PAGE,
    [query]
  );
  const showSfyTease = sfyTeaseUnseen && !isPLAReferer;

  const products = getProductsFromPaginatedResponse(data);

  if (isLoading) {
    return <LoadingBall />;
  }

  if (!products || !products.length) {
    return <EmptyResultsList />;
  }

  return (
    <InfiniteScroll
      onEndReached={fetchNextPage}
      hasMore={hasNextPage}
      loading={isFetchingNextPage}
      LoadingComponent={
        <div className={styles.loaderWrapper}>
          <LoadingBall />
        </div>
      }
    >
      <SFYSignUpSuccessModal />
      <ol className={styles.productGrid}>
        {products.map((product, index) => (
          <React.Fragment key={`${product.id}-${product.is_boosted}`}>
            <li className={styles.listItem}>
              <ProductImpressionHandler
                index={index}
                isLastItem={products.length === index + 1 && !hasNextPage}
                pageNumber={Math.floor(index / ITEMS_PER_PAGE) + 1}
              >
                <BrowseProductCard
                  slug={product.slug}
                  pricing={product.pricing}
                  sizes={product.sizes}
                  pictures={product.pictures}
                  isBoosted={product.is_boosted}
                  brandName={product.brand_name}
                  brandId={product.brand_id}
                  attributes={product.attributes}
                  id={product.id}
                  isLiked={product.is_liked}
                  disableLazyLoading={index < 4}
                  handleClick={() =>
                    handleProductClick({
                      index,
                      id: product.id,
                      isBoosted: product.is_boosted,
                      pageNumber: Math.floor(index / ITEMS_PER_PAGE) + 1,
                    })
                  }
                />
              </ProductImpressionHandler>
            </li>
            {index + 1 === ITEMS_PER_PAGE && showSfyTease && (
              <ExperimentWrapper experiment="web_7217_tease_sfy_search">
                <TeaseSuggestedForYou
                  className={styles.banner}
                  trackingEvent="search_signup_banner"
                  ref={teaseSFYRef}
                />
              </ExperimentWrapper>
            )}
          </React.Fragment>
        ))}
      </ol>
    </InfiniteScroll>
  );
}
